import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Wallet } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";

/**
 * Wallet top-up — Step 1: Amount Picker (full page).
 *
 * Matches the "Add money to wallet" screen the user shared. On Proceed
 * we stash the chosen amount in sessionStorage and navigate to the
 * Payment Information page (Step 2).
 *
 * Bonus tiles mirror TOPUP_PACKS on the backend (see server.py). The
 * five bonus tiers below (5% / 10% / 12% / 15% / 20% Extra) are
 * marketing labels only — actual bonus math is computed server-side
 * on order create via `_bonus_for_amount`.
 */

// Preset tiles matching the user's mock — order matters for the 3-col grid.
const TILE_AMOUNTS = [
  { amount: 100 },
  { amount: 200 },
  { amount: 500,   badge: "Most Popular", bonusLabel: "5% Extra" },
  { amount: 1000,  bonusLabel: "5% Extra" },
  { amount: 2000,  bonusLabel: "10% Extra" },
  { amount: 3000,  bonusLabel: "10% Extra" },
  { amount: 4000,  bonusLabel: "12% Extra" },
  { amount: 8000,  bonusLabel: "12% Extra" },
  { amount: 15000, bonusLabel: "15% Extra" },
  { amount: 20000, bonusLabel: "15% Extra" },
  { amount: 50000, bonusLabel: "20% Extra" },
  { amount: 100000,bonusLabel: "20% Extra" },
];

export default function WalletTopUpAmountPage() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const { showingUsd, usd_to_inr } = useCurrency();
  const [amount, setAmount] = useState(200);
  const [balance, setBalance] = useState(0);
  const currency = showingUsd ? "USD" : "INR";
  const symbol = showingUsd ? "$" : "₹";
  const rate = showingUsd && usd_to_inr ? usd_to_inr : 1;

  useEffect(() => {
    if (!user) { navigate("/login?next=/wallet/topup"); return; }
    checkAuth?.();
    api.get("/wallet/balance").then(({ data }) => setBalance(Number(data.balance || 0))).catch(() => {});
  }, [user, navigate, checkAuth]);

  const formatDisplay = (inrAmount) => {
    if (showingUsd && rate) return `${symbol}${(inrAmount / rate).toFixed(2)}`;
    return `${symbol}${inrAmount.toLocaleString("en-IN")}`;
  };

  // Convert an INR-denominated tile amount to a compact display label.
  // For USD customers: `$5.27` for ₹500, `$105.32` for ₹100000 — always
  // 2 decimals so buyers know the exact conversion. Backend keeps INR
  // as the source of truth (the amount stashed in sessionStorage stays
  // ₹) so the coupon / bonus math + wallet credit are unchanged.
  const formatTile = (inrAmount) => {
    if (showingUsd && rate) return `$${(inrAmount / rate).toFixed(2)}`;
    return `₹${inrAmount.toLocaleString("en-IN")}`;
  };

  // Quick-add chips: for INR show +₹50 / +₹100, for USD show +$1 / +$5
  // (rounded USD amounts that translate back to sensible INR bumps).
  const QUICK_ADD_INR = [50, 100];
  const QUICK_ADD_USD = [1, 5];   // USD amounts → converted to INR before mutating state
  const quickAdd = showingUsd ? QUICK_ADD_USD : QUICK_ADD_INR;

  const bumpAmount = (delta) => {
    // In USD mode, `delta` is USD; convert to INR before applying.
    const deltaInr = showingUsd ? Math.round(delta * rate) : delta;
    setAmount((a) => (a || 0) + deltaInr);
  };

  const proceed = () => {
    if (!amount || amount < 50) { toast.error(`Minimum top-up is ${symbol}${showingUsd ? (50 / rate).toFixed(2) : 50}`); return; }
    if (amount > 100000) { toast.error(`Maximum top-up is ${symbol}${showingUsd ? (100000 / rate).toFixed(2) : "1,00,000"}`); return; }
    sessionStorage.setItem("wallet_topup_amount_inr", String(amount));
    sessionStorage.setItem("wallet_topup_currency", currency);
    navigate("/wallet/topup/pay");
  };

  return (
    <div className="min-h-screen bg-white" data-testid="wallet-topup-amount-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-slate-50 hover:bg-slate-100 flex items-center justify-center transition"
            data-testid="topup-back-btn"
          >
            <ArrowLeft size={18} className="text-slate-700" />
          </button>
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Add money to wallet</h1>
        </div>
        <div className="flex items-center gap-2 text-slate-700 text-sm font-medium">
          <Wallet size={16} className="text-slate-500" />
          <span data-testid="topup-balance-header">{formatDisplay(balance)}</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-32">
        {/* Amount input */}
        <div className="mt-6">
          <p className="text-slate-500 text-sm">Enter Amount here</p>
          <div className="mt-2 flex items-center justify-between gap-3">
            <div className="flex items-baseline">
              <span className="text-3xl font-semibold text-slate-900 mr-1">{symbol}</span>
              <input
                type="number"
                value={showingUsd && rate ? Number((amount / rate).toFixed(2)) : amount}
                onChange={(e) => {
                  const v = Math.max(0, Number(e.target.value) || 0);
                  // In USD mode, buyer types USD — persist INR internally.
                  setAmount(showingUsd && rate ? Math.round(v * rate) : Math.floor(v));
                }}
                className="text-3xl font-semibold text-slate-900 bg-transparent border-none outline-none focus:ring-0 w-32 placeholder:text-slate-300"
                placeholder="0"
                inputMode="decimal"
                step={showingUsd ? "0.01" : "1"}
                data-testid="topup-amount-input"
              />
            </div>
            <div className="flex gap-2">
              {quickAdd.map((v) => (
                <button
                  key={v}
                  onClick={() => bumpAmount(v)}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50"
                  data-testid={`topup-add-${v}-btn`}
                >
                  + {symbol}{v}
                </button>
              ))}
            </div>
          </div>
          <div className="h-px bg-slate-100 mt-3" />
        </div>

        {/* Amount tiles grid */}
        <div className="mt-5 grid grid-cols-3 gap-3" data-testid="topup-amount-tiles">
          {TILE_AMOUNTS.map((tile) => {
            const active = amount === tile.amount;
            return (
              <button
                key={tile.amount}
                onClick={() => setAmount(tile.amount)}
                className={`relative rounded-2xl border transition p-4 flex flex-col items-center justify-center overflow-hidden ${
                  active
                    ? "border-[#E5B94D] bg-[#FFFBED] shadow-[0_2px_8px_rgba(229,185,77,0.15)]"
                    : "border-slate-100 bg-white hover:border-slate-200"
                }`}
                style={{ minHeight: 88 }}
                data-testid={`topup-tile-${tile.amount}`}
              >
                {tile.badge && (
                  <div className="absolute top-0 right-0 bg-orange-500 text-white text-[9px] font-semibold px-2 py-0.5 rounded-bl-lg tracking-wide">
                    {tile.badge}
                  </div>
                )}
                <div className="text-slate-900 font-semibold text-base">{formatTile(tile.amount)}</div>
                {tile.bonusLabel && (
                  <div className="absolute bottom-0 left-0 right-0 py-1 text-center bg-[#E9F7EF] text-[#166534] text-[10px] font-medium">
                    {tile.bonusLabel}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </main>

      {/* Proceed CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-5 py-4 z-40" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 16px)" }}>
        <div className="max-w-2xl mx-auto">
          <button
            onClick={proceed}
            disabled={!amount || amount < 50}
            className="w-full bg-[#F0D65B] hover:bg-[#E5C947] disabled:opacity-50 disabled:cursor-not-allowed text-slate-900 font-semibold py-4 rounded-2xl transition"
            data-testid="topup-proceed-btn"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
}
